/**
 * ONE TAP — raw input.
 *
 * Turns a keyboard, a mouse and a gamepad into one `InputCommand` per simulation tick.
 *
 * Three things here are load-bearing for how the game feels:
 *
 *  1. **Look is integrated the instant the event arrives, not at tick rate.** The mouse
 *     handler folds every `movementX/Y` straight into `yaw`/`pitch`, and the camera reads
 *     those fields every rendered frame. So aim has zero added latency at 144Hz even
 *     though the simulation only samples at 30Hz, and — because the deltas are summed
 *     rather than snapshotted — no mouse motion is ever dropped between ticks. That
 *     matters more here than in most shooters: a headshot is the whole game.
 *  2. **Buttons are reported as held state, never as edges.** The simulation derives its
 *     own press edges (`pressedEdge` in sim/gun.ts) from consecutive commands, which is
 *     what lets the server replay a client's input stream and get the identical result.
 *     Synthesising edges here would desync prediction the first time a packet was lost.
 *  3. **Pointer lock is treated as a resource that can be refused.** Browsers rate-limit
 *     re-entry after the user presses Escape (~1.25s in Chromium) and reject the request
 *     outright otherwise. Hammering `requestPointerLock` in that window gets the page
 *     put in a longer penalty box, so we back off and tell the player what is happening
 *     instead of silently doing nothing.
 *
 * The class owns no DOM of its own — the pause/click-to-play overlay lives in game.ts,
 * driven by the `onLockChange` / `onLockBlocked` callbacks — so it stays testable and
 * cannot fight the UI module for the screen.
 */

import { MOUSE_SENSITIVITY_DEFAULT, PITCH_LIMIT } from '@shared/constants';
import { TAU, clamp } from '@shared/math';
import type { InputCommand } from '@shared/types';
import {
  ACTIONS,
  ACTION_BY_ID,
  BindingMap,
  DEFAULT_BINDINGS,
  keyCodeOf,
  mouseCode,
  padCode,
  wheelCode,
  type ActionId,
  type Bindings,
  type InputCode,
} from './bindings';

// ---------------------------------------------------------------------------
// Tuning. These are client feel/robustness numbers, not gameplay numbers — nothing here
// changes what the simulation does, only how a physical device is read.
// ---------------------------------------------------------------------------

/**
 * Chromium refuses a pointer-lock request made too soon after the user pressed Escape.
 * The documented window is 1.25s; we wait a little longer so the retry is never the one
 * that gets rejected and restarts the clock.
 */
const LOCK_EXIT_COOLDOWN_MS = 1400;
/** Backoff applied when the browser actually rejects a request, rather than pre-emptively. */
const LOCK_ERROR_COOLDOWN_MS = 2000;

/**
 * A single mouse event larger than this is not a human — it is the spurious jump some
 * browsers emit on the first move after acquiring lock, or a driver hiccup. Dropping the
 * event entirely is right: clamping it would still spin the view a quarter turn.
 */
const MAX_MOUSE_DELTA_PX = 480;

/** Radial deadzone, as a fraction of full stick deflection. */
const GAMEPAD_DEADZONE_DEFAULT = 0.16;
/** Look speed in radians/second at full right-stick deflection. */
const GAMEPAD_LOOK_SPEED_DEFAULT = 3.4;
/**
 * Response curve exponent for the look stick. >1 gives fine control near centre and full
 * speed at the rim, which is the only way a stick competes with a mouse for tracking.
 */
const GAMEPAD_LOOK_EXPO = 2.0;
/** Analogue triggers report `value`; treat them as pressed past this. */
const GAMEPAD_TRIGGER_THRESHOLD = 0.4;
/** Sticks/buttons quieter than this do not count as "the player is using a pad". */
const GAMEPAD_ACTIVITY_EPSILON = 0.25;
/** How long a pad stays "active" after the last input, for the auto-hide of the lock prompt. */
const GAMEPAD_ACTIVE_MS = 4000;

/** Standard Gamepad axis indices. Left stick moves, right stick looks — on every pad. */
const AXIS_MOVE_X = 0;
const AXIS_MOVE_Y = 1;
const AXIS_LOOK_X = 2;
const AXIS_LOOK_Y = 3;

/** Sensitivity guard rails, in radians per pixel of raw mouse motion. */
const MIN_SENSITIVITY = 0.0001;
const MAX_SENSITIVITY = 0.02;

function wrapYaw(yaw: number): number {
  let y = yaw % TAU;
  if (y > Math.PI) y -= TAU;
  if (y <= -Math.PI) y += TAU;
  return y;
}

/** True when the event landed in something the player is typing into. */
function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.tagName !== 'string') return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return el.isContentEditable === true;
}

/** Radial deadzone with rescale, so the usable range still reaches 1.0 at the rim. */
function applyDeadzone(x: number, y: number, deadzone: number, out: { x: number; y: number }): void {
  const mag = Math.hypot(x, y);
  if (mag <= deadzone || mag <= 0) {
    out.x = 0;
    out.y = 0;
    return;
  }
  const scaled = Math.min(1, (mag - deadzone) / (1 - deadzone)) / mag;
  out.x = x * scaled;
  out.y = y * scaled;
}

export type BindingCapture = (code: InputCode | null) => void;

export class InputManager {
  /** Radians of view rotation per pixel of raw mouse motion. */
  sensitivity = MOUSE_SENSITIVITY_DEFAULT;
  invertY = false;

  gamepadEnabled = true;
  gamepadDeadzone = GAMEPAD_DEADZONE_DEFAULT;
  gamepadLookSpeed = GAMEPAD_LOOK_SPEED_DEFAULT;

  /** Current view angles. The camera reads these every frame; the sim reads them per tick. */
  yaw = 0;
  pitch = 0;

  /**
   * Master gate. Game sets this true only while a match is on screen, so the lobby's
   * text fields never eat a `KeyW` and the menu never fires a PULSE.
   */
  enabled = false;

  /** Fired on every pointer-lock transition. */
  onLockChange: (locked: boolean) => void = () => {};
  /**
   * Fired when the browser refused to give us the mouse. `retryInMs` is how long the
   * caller should tell the player to wait before clicking again.
   */
  onLockBlocked: (reason: string, retryInMs: number) => void = () => {};
  /** Non-simulation actions (menu, scoreboard) are dispatched as edges. */
  onAction: (action: ActionId, down: boolean) => void = () => {};
  /** Any physical input at all. Used to unlock the WebAudio context on first gesture. */
  onAnyInput: () => void = () => {};

  private readonly canvas: HTMLElement;
  private readonly map = new BindingMap(DEFAULT_BINDINGS);

  /** Codes physically down right now (keyboard + mouse). */
  private readonly held = new Set<InputCode>();
  /** Wheel "presses" survive exactly one `sample()`, since a wheel has no up event. */
  private readonly wheelPulse = new Set<InputCode>();
  /** Gamepad buttons down, refreshed once per rendered frame. */
  private readonly padHeld = new Set<InputCode>();

  private padMoveX = 0;
  private padMoveZ = 0;
  private padIndex = -1;
  private padActivityAtMs = -Infinity;
  private readonly stick = { x: 0, y: 0 };

  private locked = false;
  private lockRequestPending = false;
  private lockBlockedUntilMs = 0;
  private swallowNextMouseMove = false;
  private lastBlockedMessage = '';

  private capture: BindingCapture | null = null;
  private disposed = false;

  private readonly onKeyDown: (e: KeyboardEvent) => void;
  private readonly onKeyUp: (e: KeyboardEvent) => void;
  private readonly onMouseDown: (e: MouseEvent) => void;
  private readonly onMouseUp: (e: MouseEvent) => void;
  private readonly onMouseMove: (e: MouseEvent) => void;
  private readonly onWheel: (e: WheelEvent) => void;
  private readonly onContextMenu: (e: Event) => void;
  private readonly onPointerLockChange: () => void;
  private readonly onPointerLockError: () => void;
  private readonly onBlur: () => void;
  private readonly onVisibility: () => void;

  constructor(canvasEl: HTMLElement) {
    this.canvas = canvasEl;

    this.onKeyDown = (e) => this.handleKey(e, true);
    this.onKeyUp = (e) => this.handleKey(e, false);
    this.onMouseDown = (e) => this.handleMouseButton(e, true);
    this.onMouseUp = (e) => this.handleMouseButton(e, false);
    this.onMouseMove = (e) => this.handleMouseMove(e);
    this.onWheel = (e) => this.handleWheel(e);
    this.onContextMenu = (e) => {
      // RMB is THROW. A context menu mid-pass would be a lost round.
      if (this.locked || this.enabled) e.preventDefault();
    };
    this.onPointerLockChange = () => this.handleLockChange();
    this.onPointerLockError = () => {
      this.lockRequestPending = false;
      this.blockLock('The browser refused to capture the mouse. Click again.', LOCK_ERROR_COOLDOWN_MS);
    };
    this.onBlur = () => this.clearHeld();
    this.onVisibility = () => {
      if (document.visibilityState !== 'visible') this.clearHeld();
    };

    window.addEventListener('keydown', this.onKeyDown, { passive: false });
    window.addEventListener('keyup', this.onKeyUp, { passive: true });
    window.addEventListener('mousedown', this.onMouseDown, { passive: true });
    window.addEventListener('mouseup', this.onMouseUp, { passive: true });
    document.addEventListener('mousemove', this.onMouseMove, { passive: true });
    window.addEventListener('wheel', this.onWheel, { passive: false });
    this.canvas.addEventListener('contextmenu', this.onContextMenu);
    document.addEventListener('pointerlockchange', this.onPointerLockChange);
    document.addEventListener('pointerlockerror', this.onPointerLockError);
    window.addEventListener('blur', this.onBlur);
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  // -------------------------------------------------------------------------
  // Bindings
  // -------------------------------------------------------------------------

  get bindings(): Readonly<Bindings> {
    return this.map.bindings;
  }

  setBindings(b: Readonly<Bindings>): void {
    this.map.set(b);
    // A code that was down under the old table has no meaning under the new one.
    this.clearHeld();
  }

  /**
   * Swallow the next physical input and hand its code to `cb` instead of the game.
   * Escape cancels and reports `null`. Returns a cancel function.
   */
  beginCapture(cb: BindingCapture): () => void {
    this.capture = cb;
    this.clearHeld();
    return () => {
      if (this.capture === cb) this.capture = null;
    };
  }

  get capturingBinding(): boolean {
    return this.capture !== null;
  }

  // -------------------------------------------------------------------------
  // Pointer lock
  // -------------------------------------------------------------------------

  isPointerLocked(): boolean {
    return document.pointerLockElement === this.canvas;
  }

  /** Milliseconds until another `requestLock()` has a chance of succeeding. */
  lockCooldownMs(): number {
    return Math.max(0, this.lockBlockedUntilMs - performance.now());
  }

  /** Last explanation handed to `onLockBlocked`, for a UI that renders it lazily. */
  get lockBlockedReason(): string {
    return this.lastBlockedMessage;
  }

  /**
   * Ask for the mouse. Must be called from inside a user gesture.
   *
   * Tries `unadjustedMovement` first — that is the raw, OS-acceleration-free delta an
   * aim-sensitive game wants — and silently falls back to the ordinary request where it
   * is unsupported. A rejection that is *not* the unsupported-option case is the browser
   * rate-limiting us, and is reported rather than retried.
   */
  requestLock(): void {
    if (this.disposed || this.isPointerLocked() || this.lockRequestPending) return;

    const remaining = this.lockCooldownMs();
    if (remaining > 0) {
      this.onLockBlocked(this.lastBlockedMessage || 'Mouse capture is cooling down.', remaining);
      return;
    }

    const el = this.canvas as HTMLElement & {
      requestPointerLock(options?: { unadjustedMovement?: boolean }): Promise<void> | void;
    };

    this.lockRequestPending = true;
    let result: Promise<void> | void;
    try {
      result = el.requestPointerLock({ unadjustedMovement: true });
    } catch {
      result = undefined;
      this.lockRequestPending = false;
      this.plainLock();
      return;
    }

    if (result && typeof (result as Promise<void>).then === 'function') {
      (result as Promise<void>).then(
        () => {
          this.lockRequestPending = false;
        },
        (err: unknown) => {
          this.lockRequestPending = false;
          // `NotSupportedError` means only the raw-motion option was refused; the plain
          // request is still worth one try. Anything else is a real denial.
          const name = (err as { name?: string } | null)?.name ?? '';
          if (name === 'NotSupportedError') this.plainLock();
          else this.blockLock(this.describeLockError(err), LOCK_ERROR_COOLDOWN_MS);
        },
      );
      return;
    }

    // Legacy void-returning implementation: success or failure arrives as an event.
    this.lockRequestPending = false;
  }

  /** Give the mouse back. Safe to call when we never had it. */
  releaseLock(): void {
    if (this.isPointerLocked()) document.exitPointerLock();
  }

  private plainLock(): void {
    if (this.disposed || this.isPointerLocked()) return;
    const el = this.canvas as HTMLElement & { requestPointerLock(): Promise<void> | void };
    this.lockRequestPending = true;
    let result: Promise<void> | void;
    try {
      result = el.requestPointerLock();
    } catch (err) {
      this.lockRequestPending = false;
      this.blockLock(this.describeLockError(err), LOCK_ERROR_COOLDOWN_MS);
      return;
    }
    if (result && typeof (result as Promise<void>).then === 'function') {
      (result as Promise<void>).then(
        () => {
          this.lockRequestPending = false;
        },
        (err: unknown) => {
          this.lockRequestPending = false;
          this.blockLock(this.describeLockError(err), LOCK_ERROR_COOLDOWN_MS);
        },
      );
    } else {
      this.lockRequestPending = false;
    }
  }

  private describeLockError(err: unknown): string {
    const name = (err as { name?: string } | null)?.name ?? '';
    if (name === 'SecurityError') {
      return 'The browser is throttling mouse capture. Wait a moment, then click again.';
    }
    if (name === 'WrongDocumentError' || name === 'InvalidStateError') {
      return 'The window lost focus. Click the game, then click again.';
    }
    return 'The browser refused to capture the mouse. Click again.';
  }

  private blockLock(reason: string, cooldownMs: number): void {
    this.lastBlockedMessage = reason;
    this.lockBlockedUntilMs = Math.max(this.lockBlockedUntilMs, performance.now() + cooldownMs);
    this.onLockBlocked(reason, this.lockCooldownMs());
  }

  private handleLockChange(): void {
    const locked = this.isPointerLocked();
    if (locked === this.locked) return;
    this.locked = locked;

    if (locked) {
      this.lockBlockedUntilMs = 0;
      this.lastBlockedMessage = '';
      // The first move after acquiring lock is frequently a large synthetic delta.
      this.swallowNextMouseMove = true;
    } else {
      this.clearHeld();
      // Escape-to-exit starts the browser's re-entry timer whether we like it or not.
      this.lockBlockedUntilMs = performance.now() + LOCK_EXIT_COOLDOWN_MS;
      this.lastBlockedMessage = 'Mouse released. Click to resume.';
    }

    this.onLockChange(locked);
  }

  // -------------------------------------------------------------------------
  // Event handling
  // -------------------------------------------------------------------------

  /** True when physical input should reach the game rather than the page. */
  private get capturing(): boolean {
    if (!this.enabled || this.disposed) return false;
    return this.locked || this.padActive;
  }

  /** A pad counts as "in use" for a few seconds after its last real input. */
  get padActive(): boolean {
    return this.gamepadEnabled && performance.now() - this.padActivityAtMs < GAMEPAD_ACTIVE_MS;
  }

  private handleKey(e: KeyboardEvent, down: boolean): void {
    if (this.disposed) return;
    const code = keyCodeOf(e);
    if (!code) return;

    if (down) this.onAnyInput();

    if (this.capture) {
      if (!down) return;
      e.preventDefault();
      const cb = this.capture;
      this.capture = null;
      cb(code === 'Escape' ? null : code);
      return;
    }

    // Never steal keys from a lobby text field, and never from a browser shortcut.
    if (isEditableTarget(e.target)) return;
    if (!this.enabled) return; // outside a match the keyboard belongs to the menus
    if (e.ctrlKey && code !== 'ControlLeft' && code !== 'ControlRight') return;
    if (e.metaKey && code !== 'MetaLeft' && code !== 'MetaRight') return;
    if (e.altKey && code !== 'AltLeft' && code !== 'AltRight') return;

    if (down && e.repeat) return; // held-state is already correct; auto-repeat is noise

    const actions = this.map.actionsFor(code);
    if (!actions || actions.length === 0) return;

    // Tab would move focus out of the canvas; Space would scroll. Both are fatal in a
    // shooter, so any bound key is consumed *while we own the pointer*. With the pointer
    // released the pause overlay is on screen and normal tab-navigation has to keep working.
    if (this.locked) e.preventDefault();

    if (down) this.held.add(code);
    else this.held.delete(code);

    this.dispatchNonSimActions(actions, code, down);
  }

  private handleMouseButton(e: MouseEvent, down: boolean): void {
    if (this.disposed) return;
    const code = mouseCode(e.button);

    if (down) this.onAnyInput();

    if (this.capture) {
      if (!down) return;
      const cb = this.capture;
      this.capture = null;
      cb(code);
      return;
    }

    // Mouse buttons only mean anything once we own the pointer; otherwise they belong
    // to the menus.
    if (!this.locked) {
      if (!down) this.held.delete(code);
      return;
    }

    if (down) this.held.add(code);
    else this.held.delete(code);

    const actions = this.map.actionsFor(code);
    if (actions) this.dispatchNonSimActions(actions, code, down);
  }

  private handleMouseMove(e: MouseEvent): void {
    if (this.disposed || !this.locked) return;

    if (this.swallowNextMouseMove) {
      this.swallowNextMouseMove = false;
      return;
    }

    const dx = e.movementX;
    const dy = e.movementY;
    if (!Number.isFinite(dx) || !Number.isFinite(dy)) return;
    if (Math.abs(dx) > MAX_MOUSE_DELTA_PX || Math.abs(dy) > MAX_MOUSE_DELTA_PX) return;
    if (dx === 0 && dy === 0) return;

    const sens = clamp(this.sensitivity, MIN_SENSITIVITY, MAX_SENSITIVITY);
    // Yaw grows counter-clockwise (see math.forwardFromYaw), so moving the mouse right
    // must subtract. Pitch is positive looking up.
    this.yaw = wrapYaw(this.yaw - dx * sens);
    const dp = dy * sens * (this.invertY ? 1 : -1);
    this.pitch = clamp(this.pitch + dp, -PITCH_LIMIT, PITCH_LIMIT);
  }

  private handleWheel(e: WheelEvent): void {
    if (this.disposed) return;
    const code = wheelCode(e.deltaY);

    if (this.capture) {
      e.preventDefault();
      const cb = this.capture;
      this.capture = null;
      cb(code);
      return;
    }

    if (!this.locked) return;
    const actions = this.map.actionsFor(code);
    if (!actions || actions.length === 0) return;
    e.preventDefault();
    this.onAnyInput();
    this.wheelPulse.add(code);
    this.dispatchNonSimActions(actions, code, true);
  }

  /**
   * Scoreboard and menu never reach the simulation, so they are delivered as edges here.
   * Everything with a `Btn` bit stays pure held-state.
   */
  private dispatchNonSimActions(actions: readonly ActionId[], _code: InputCode, down: boolean): void {
    for (const id of actions) {
      if (ACTION_BY_ID[id].bit !== 0) continue;
      if (id === 'menu' && !down) continue; // menu is a press, not a toggle
      this.onAction(id, down);
    }
  }

  private clearHeld(): void {
    // Release every non-sim action that was down, or the scoreboard stays stuck open.
    for (const code of this.held) {
      const actions = this.map.actionsFor(code);
      if (!actions) continue;
      for (const id of actions) {
        if (ACTION_BY_ID[id].bit === 0 && id !== 'menu') this.onAction(id, false);
      }
    }
    this.held.clear();
    this.wheelPulse.clear();
    this.padHeld.clear();
    this.padMoveX = 0;
    this.padMoveZ = 0;
  }

  // -------------------------------------------------------------------------
  // Gamepad
  // -------------------------------------------------------------------------

  /**
   * Called once per *rendered* frame, before the tick loop.
   *
   * The pad has to be polled (there are no events) and its look stick has to integrate
   * against the frame's real dt, or turning speed would change with framerate.
   */
  beginFrame(dtSec: number): void {
    if (this.disposed) return;
    if (!this.gamepadEnabled) {
      if (this.padHeld.size > 0) this.padHeld.clear();
      this.padMoveX = 0;
      this.padMoveZ = 0;
      return;
    }

    const pad = this.pickGamepad();
    if (!pad) {
      this.padHeld.clear();
      this.padMoveX = 0;
      this.padMoveZ = 0;
      this.padIndex = -1;
      return;
    }
    this.padIndex = pad.index;

    let activity = false;

    this.padHeld.clear();
    for (let i = 0; i < pad.buttons.length && i < 20; i++) {
      const b = pad.buttons[i];
      if (!b) continue;
      const pressed = b.pressed || b.value > GAMEPAD_TRIGGER_THRESHOLD;
      if (!pressed) continue;
      this.padHeld.add(padCode(i));
      activity = true;
    }

    const ax = pad.axes;
    applyDeadzone(ax[AXIS_MOVE_X] ?? 0, ax[AXIS_MOVE_Y] ?? 0, this.gamepadDeadzone, this.stick);
    this.padMoveX = this.stick.x;
    this.padMoveZ = -this.stick.y; // stick Y is positive downward; +moveZ is forward
    if (Math.hypot(this.padMoveX, this.padMoveZ) > GAMEPAD_ACTIVITY_EPSILON) activity = true;

    applyDeadzone(ax[AXIS_LOOK_X] ?? 0, ax[AXIS_LOOK_Y] ?? 0, this.gamepadDeadzone, this.stick);
    const lookMag = Math.hypot(this.stick.x, this.stick.y);
    if (lookMag > GAMEPAD_ACTIVITY_EPSILON) activity = true;

    // Claim "the player is on a pad" before reading `capturing`, so the very first flick
    // of the stick turns the view instead of being eaten by the activity window.
    if (activity) {
      this.padActivityAtMs = performance.now();
      this.onAnyInput();
    }

    if (lookMag > 0 && this.capturing) {
      // Expo on the *magnitude* keeps the stick's direction honest; applying it per-axis
      // would bend diagonals, which is exactly where tracking matters.
      const curved = Math.pow(lookMag, GAMEPAD_LOOK_EXPO) / lookMag;
      const rate = this.gamepadLookSpeed * dtSec;
      this.yaw = wrapYaw(this.yaw - this.stick.x * curved * rate);
      const dp = this.stick.y * curved * rate * (this.invertY ? 1 : -1);
      this.pitch = clamp(this.pitch + dp, -PITCH_LIMIT, PITCH_LIMIT);
    }

    // Menu/scoreboard edges from the pad, since they never reach the simulation.
    this.pollPadEdges();
  }

  private readonly padEdgeState = new Map<ActionId, boolean>();

  private pollPadEdges(): void {
    if (!this.enabled) return;
    for (const a of ACTIONS) {
      if (a.bit !== 0) continue;
      let down = false;
      for (const code of this.map.codesFor(a.id)) {
        if (this.padHeld.has(code)) {
          down = true;
          break;
        }
      }
      const was = this.padEdgeState.get(a.id) === true;
      if (down === was) continue;
      this.padEdgeState.set(a.id, down);
      if (a.id === 'menu' && !down) continue;
      this.onAction(a.id, down);
    }
  }

  private pickGamepad(): Gamepad | null {
    const nav = navigator as Navigator & { getGamepads?: () => (Gamepad | null)[] };
    if (typeof nav.getGamepads !== 'function') return null;
    let pads: (Gamepad | null)[];
    try {
      pads = nav.getGamepads();
    } catch {
      return null;
    }
    // Stick with the pad we were already reading if it is still there, so a second
    // controller plugged in mid-match cannot steal the stick.
    if (this.padIndex >= 0) {
      const current = pads[this.padIndex];
      if (current && current.connected) return current;
    }
    for (const p of pads) {
      if (p && p.connected && p.buttons.length > 0) return p;
    }
    return null;
  }

  /** Index of the pad currently being read, or -1. */
  get activeGamepadIndex(): number {
    return this.padIndex;
  }

  // -------------------------------------------------------------------------
  // Sampling
  // -------------------------------------------------------------------------

  private isCodeDown(code: InputCode): boolean {
    return this.held.has(code) || this.padHeld.has(code) || this.wheelPulse.has(code);
  }

  /** True while any control bound to `action` is down. */
  actionHeld(action: ActionId): boolean {
    for (const code of this.map.codesFor(action)) {
      if (this.isCodeDown(code)) return true;
    }
    return false;
  }

  /** Force the view to a specific orientation — used on spawn so the server's facing wins. */
  setView(yaw: number, pitch: number): void {
    if (Number.isFinite(yaw)) this.yaw = wrapYaw(yaw);
    if (Number.isFinite(pitch)) this.pitch = clamp(pitch, -PITCH_LIMIT, PITCH_LIMIT);
  }

  /**
   * Produce one tick of intent.
   *
   * `dt` is always `TICK_DT` — the simulation is fixed-step on both ends — and
   * `clientTimeMs` is the render timestamp the server uses for lag-compensated rewind.
   */
  sample(seq: number, dt: number, clientTimeMs: number): InputCommand {
    const active = this.capturing;

    let moveX = 0;
    let moveZ = 0;
    let buttons = 0;

    if (active) {
      if (this.actionHeld('moveRight')) moveX += 1;
      if (this.actionHeld('moveLeft')) moveX -= 1;
      if (this.actionHeld('moveForward')) moveZ += 1;
      if (this.actionHeld('moveBack')) moveZ -= 1;

      moveX += this.padMoveX;
      moveZ += this.padMoveZ;

      const mag = Math.hypot(moveX, moveZ);
      if (mag > 1) {
        moveX /= mag;
        moveZ /= mag;
      }

      for (const entry of this.map.buttonActions) {
        if (this.actionHeld(entry.action)) buttons |= entry.bit;
      }
    }

    // A wheel notch is one discrete event with no release; it lives for exactly one tick.
    if (this.wheelPulse.size > 0) this.wheelPulse.clear();

    return {
      seq,
      dt,
      moveX,
      moveZ,
      yaw: this.yaw,
      pitch: this.pitch,
      buttons,
      clientTimeMs,
    };
  }

  // -------------------------------------------------------------------------

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    this.releaseLock();

    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('mousedown', this.onMouseDown);
    window.removeEventListener('mouseup', this.onMouseUp);
    document.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('wheel', this.onWheel);
    this.canvas.removeEventListener('contextmenu', this.onContextMenu);
    document.removeEventListener('pointerlockchange', this.onPointerLockChange);
    document.removeEventListener('pointerlockerror', this.onPointerLockError);
    window.removeEventListener('blur', this.onBlur);
    document.removeEventListener('visibilitychange', this.onVisibility);

    this.held.clear();
    this.wheelPulse.clear();
    this.padHeld.clear();
    this.padEdgeState.clear();
    this.capture = null;
    this.onLockChange = () => {};
    this.onLockBlocked = () => {};
    this.onAction = () => {};
    this.onAnyInput = () => {};
  }
}
